import { TanStackPostalCodeField } from "@sellar-io/components/adapters";
import { useCurrentDate, Button, InputNote } from "@sellar-io/components";
import { CountryCodeDictionary, validateUpsWeight } from "@sellar-io/common";
import {
  AllFulfillmentTypesType,
  ErrorSchemaType,
  OrderGetResponseType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import { HttpError } from "~/utils/httpClient";
import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { SpinnerLegacy } from "../../../../components";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { DeliveryAddressType } from "../OrderOnBehalfOf.shared";
import { OrderFulfillmentType } from "@sellar-io/api-v2/types";
import { UPS_WEIGHT_CHANGE_DATE } from "../../../../constants/sellarConstants.shared";
import { Alert } from "../../../../components/alert/Alert";
import { DeliveryInstructionAlert } from "../../../../components/deliveryInstructionAlert/DeliveryInstructionAlert";
import { useDeliveryOptions } from "../../../../hooks/useDeliveryOptions";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { SellarDistributionFulfillmentChangeFormFragment } from "../../modals/SellarDistributionFulfillmentChangeFormFragment/SellarDistributionFulfillmentChangeFormFragment";
import { validateFulfillmentTypeChange } from "../../modals/SellarDistributionFulfillmentChangeFormFragment/lib/validateFulfillmentTypeChange";
import { getIsSwitchingBuyerOrderFromSellarDistribution } from "../../modals/SellarDistributionFulfillmentChangeFormFragment/lib/isSwitchingBuyerOrderFromSD";

export type ChangeDeliveryDetailsType = {
  deliveryAddress: DeliveryAddressType | undefined;
  fulfillmentType: OrderFulfillmentType;
  deliveryNotes: string | null;
  fulfillmentChangeReason: OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReason"];
  fulfillmentChangeReasonMessage: NonNullable<
    OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReasonMessage"]
  >;
  fulfillmentChangeAcknowledged: boolean;
};

export const ChangeDeliveryDetailsDialog = ({
  active,
  onDismiss,
  address: initAddress,
  fulfillmentType: initFulfillmentType,
  deliveryNotes: initDeliveryNotes,
  isFinalised,
  onSubmit,
  error,
  loading,
  description,
  title,
  orderDate,
  items,
  initiator,
  buyerName,
}: {
  active: boolean;
  onDismiss: () => void;
  address?: DeliveryAddressType;
  fulfillmentType: OrderFulfillmentType;
  deliveryNotes: ChangeDeliveryDetailsType["deliveryNotes"];
  isFinalised?: boolean;
  onSubmit: (data: ChangeDeliveryDetailsType) => void;
  error: HttpError<{ message: string; details: ErrorSchemaType }> | null;
  loading?: boolean;
  description?: string;
  title?: string;
  orderDate?: Date | null;
  items: OrderGetResponseType["OrderItems"];
  initiator: OrderGetResponseType["initiator"];
  buyerName: string;
}) => {
  const currentDate = useCurrentDate();
  // Check UPS weight limits
  const upsWeightValidation = validateUpsWeight(
    items,
    UPS_WEIGHT_CHANGE_DATE,
    orderDate ? new Date(orderDate) : currentDate,
  );

  const { business } = useSellarCurrentBusiness();

  const buyerPostcode = initAddress?.postalCode;

  const hiddenDeliveryOptionsWhenFinalised = (): Array<
    Extract<
      AllFulfillmentTypesType,
      "ups" | "apc" | "sellarDistribution" | "collection"
    >
  > => {
    if (!isFinalised) return [];
    if (initFulfillmentType === "sellarDistribution") {
      return ["collection"];
    }
    if (initFulfillmentType === "collection") {
      return ["sellarDistribution"];
    }
    return [];
  };

  const { isFetched: deliveryAreaIsFetched, deliveryOptions } =
    useDeliveryOptions({
      postcode: buyerPostcode,
      hiddenOptions: hiddenDeliveryOptionsWhenFinalised(),
      includeShadowRoutes: business?.type === "supplier",
    });

  const form = useForm({
    defaultValues: {
      deliveryAddress: initAddress,
      fulfillmentType: initFulfillmentType,
      deliveryNotes: initDeliveryNotes,
      fulfillmentChangeReason: undefined,
      fulfillmentChangeReasonMessage: "",
      fulfillmentChangeAcknowledged: false,
    } as ChangeDeliveryDetailsType,
    validators: {
      onSubmit: ({ value }) => {
        const validation = validateFulfillmentTypeChange({
          data: value,
          isSwitchingBuyerOrderFromSellarDistribution:
            getIsSwitchingBuyerOrderFromSellarDistribution({
              order: { fulfillmentType: initFulfillmentType, initiator },
              fulfillmentType: value.fulfillmentType,
            }),
        });
        if (!validation.success)
          return {
            form: Object.fromEntries(
              validation.errors.map(([field, error]) => [
                field,
                error.message ?? "Invalid value",
              ]),
            ),
          };
        return undefined;
      },
    },
    onSubmit: ({ value }) => onSubmit(value),
  });
  const {
    fulfillmentType,
    fulfillmentChangeReason,
    fulfillmentChangeReasonMessage,
    fulfillmentChangeAcknowledged,
  } = useStore(form.store, (state) => state.values);
  const fulfillmentChangeErrors = useStore(form.store, (state) => {
    const errors = state.errorMap.onSubmit;
    if (
      !errors ||
      typeof errors !== "object" ||
      !("form" in errors) ||
      !errors.form ||
      typeof errors.form !== "object"
    )
      return {};
    return Object.fromEntries(
      Object.entries(errors.form).map(([field, message]) => [
        field,
        { type: "required", message: String(message) },
      ]),
    );
  });

  const isUpsSelected = fulfillmentType === "ups";
  const shouldDisableUpdateButton =
    isUpsSelected && !upsWeightValidation.isValid;

  useEffect(() => {
    if (initAddress && initDeliveryNotes && initFulfillmentType) {
      form.reset({
        deliveryAddress: initAddress,
        fulfillmentType: initFulfillmentType,
        deliveryNotes: initDeliveryNotes,
        fulfillmentChangeReason: undefined,
        fulfillmentChangeReasonMessage: "",
        fulfillmentChangeAcknowledged: false,
      });
    }
  }, [form, initAddress, initDeliveryNotes, initFulfillmentType]);

  const showFulfillmentChangedAlert = fulfillmentType !== initFulfillmentType;

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => onDismiss()}
        title={title ?? "Change delivery details"}
        description={description}
      />
      <DialogContent>
        {loading ? (
          <SpinnerLegacy />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            {error && (
              <Alert
                type={"error"}
                message={error.response?.data.message ?? error.message}
              />
            )}
            <div className="flex flex-col gap-2">
              <InputLabel>Fulfillment type</InputLabel>
              <InputContainer>
                {deliveryAreaIsFetched ? (
                  <form.Field name="fulfillmentType">
                    {(field) => (
                      <InputSelect
                        name={field.name}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        required
                        onChange={(event) => {
                          field.handleChange(
                            event.target.value as OrderFulfillmentType,
                          );
                          form.setFieldValue(
                            "fulfillmentChangeReason",
                            undefined,
                          );
                          form.setFieldValue(
                            "fulfillmentChangeReasonMessage",
                            "",
                          );
                          form.setFieldValue(
                            "fulfillmentChangeAcknowledged",
                            false,
                          );
                          form.setErrorMap({ onSubmit: undefined });
                        }}
                      >
                        {deliveryOptions.map(({ name, value }) => (
                          <InputOption key={value} value={value}>
                            {name}
                          </InputOption>
                        ))}
                      </InputSelect>
                    )}
                  </form.Field>
                ) : (
                  <></>
                )}
              </InputContainer>
            </div>

            <SellarDistributionFulfillmentChangeFormFragment
              currentFulfillmentType={initFulfillmentType}
              newFulfillmentType={fulfillmentType ?? initFulfillmentType}
              initiator={initiator}
              onChange={(reason) => {
                form.setFieldValue("fulfillmentChangeReason", reason.reason);
                form.setFieldValue(
                  "fulfillmentChangeReasonMessage",
                  reason.message,
                );
                form.setFieldValue(
                  "fulfillmentChangeAcknowledged",
                  reason.acknowledged,
                );
                form.setErrorMap({ onSubmit: undefined });
              }}
              fulfillmentChangeReason={{
                reason: fulfillmentChangeReason,
                message: fulfillmentChangeReasonMessage ?? "",
                acknowledged: !!fulfillmentChangeAcknowledged,
              }}
              errors={fulfillmentChangeErrors}
              buyerName={buyerName}
            />

            {!isFinalised && (
              <div className="flex flex-col gap-2">
                <InputLabel>Delivery address</InputLabel>
                <form.Field
                  name="deliveryAddress.name"
                  validators={{
                    onSubmit: ({ value }) =>
                      !value ? "Address line 1 is required" : undefined,
                  }}
                >
                  {(field) => (
                    <>
                      <InputContainer>
                        <InputText
                          name={field.name}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          required
                          placeholder="Address line 1"
                        />
                      </InputContainer>
                      {field.state.meta.errors.length > 0 && (
                        <InputNote type="error">
                          {field.state.meta.errors.join(", ")}
                        </InputNote>
                      )}
                    </>
                  )}
                </form.Field>
                <form.Field name="deliveryAddress.street">
                  {(field) => (
                    <>
                      <InputContainer>
                        <InputText
                          name={field.name}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          placeholder="Address line 2 (optional)"
                        />
                      </InputContainer>
                      {field.state.meta.errors.length > 0 && (
                        <InputNote type="error">
                          {field.state.meta.errors.join(", ")}
                        </InputNote>
                      )}
                    </>
                  )}
                </form.Field>
                <form.Field
                  name="deliveryAddress.city"
                  validators={{
                    onSubmit: ({ value }) =>
                      !value ? "City is required" : undefined,
                  }}
                >
                  {(field) => (
                    <>
                      <InputContainer>
                        <InputText
                          name={field.name}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          required
                          placeholder="City"
                        />
                      </InputContainer>
                      {field.state.meta.errors.length > 0 && (
                        <InputNote type="error">
                          {field.state.meta.errors.join(", ")}
                        </InputNote>
                      )}
                    </>
                  )}
                </form.Field>
                <form.Field name="deliveryAddress.region">
                  {(field) => (
                    <>
                      <InputContainer>
                        <InputText
                          name={field.name}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          placeholder="Region"
                        />
                      </InputContainer>
                      {field.state.meta.errors.length > 0 && (
                        <InputNote type="error">
                          {field.state.meta.errors.join(", ")}
                        </InputNote>
                      )}
                    </>
                  )}
                </form.Field>
                <TanStackPostalCodeField
                  form={form}
                  name="deliveryAddress.postalCode"
                  countryField="deliveryAddress.country"
                />
                <form.Field
                  name="deliveryAddress.country"
                  validators={{
                    onSubmit: ({ value }) =>
                      !value ? "Country is required" : undefined,
                  }}
                >
                  {(field) => (
                    <InputContainer>
                      <InputSelect
                        name={field.name}
                        value={field.state.value ?? ""}
                        onBlur={field.handleBlur}
                        onChange={(event) =>
                          field.handleChange(
                            event.target.value as NonNullable<
                              ChangeDeliveryDetailsType["deliveryAddress"]
                            >["country"],
                          )
                        }
                        required
                        placeholder="Country"
                      >
                        {Object.entries(CountryCodeDictionary).map(
                          ([key, label]) => (
                            <InputOption key={key} value={key}>
                              {label}
                            </InputOption>
                          ),
                        )}
                      </InputSelect>
                    </InputContainer>
                  )}
                </form.Field>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <InputLabel>Delivery instructions</InputLabel>
              <form.Field name="deliveryNotes">
                {(field) => (
                  <InputContainer>
                    <InputText
                      name={field.name}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      placeholder="e.g. Gate code 1234. Leave round the back if no one is in"
                    />
                  </InputContainer>
                )}
              </form.Field>
              <DeliveryInstructionAlert />
            </div>
            {showFulfillmentChangedAlert && isFinalised && (
              <Alert
                message={""}
                title={"Order total and delivery charges are fixed"}
                type={"information"}
                className={"mt-4 w-full"}
              >
                <p>
                  This order has been finalised so any changes to the
                  fulfillment type will not be reflected in the order total
                </p>
              </Alert>
            )}
            {isUpsSelected && !upsWeightValidation.isValid && (
              <Alert
                message={""}
                title={`UPS cannot ship items over ${upsWeightValidation.maxAllowedWeight}kg`}
                type={"warning"}
                className={"mt-4 w-full"}
              >
                <p>
                  The following items exceed the UPS weight limit:{" "}
                  {upsWeightValidation.exceededItems
                    .map((item) => `${item.productName} (${item.weight}kg)`)
                    .join(", ")}
                  . Please select a different fulfillment type or remove these
                  items.
                </p>
              </Alert>
            )}
            <Button
              type="submit"
              className={"w-full"}
              disabled={shouldDisableUpdateButton}
            >
              Update
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
